import { useState } from 'react';
import { Loader2, Plus, ListPlus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePlaylists } from '@/hooks/usePlaylists';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { CreatePlaylistDialog } from '@/components/CreatePlaylistDialog';
import { PLAYLIST_KIND, MUSIC_TRACK_KIND, type MusicTrack } from '@/lib/musicTypes';
import { Card, CardContent } from '@/components/ui/card';

interface AddToPlaylistDialogProps {
  track: MusicTrack;
  trigger?: React.ReactNode;
}

export function AddToPlaylistDialog({ track, trigger }: AddToPlaylistDialogProps) {
  const [open, setOpen] = useState(false);
  const [updatingPlaylistId, setUpdatingPlaylistId] = useState<string | null>(null);
  const { user } = useCurrentUser();
  const { data: playlists, isLoading, refetch } = usePlaylists({ pubkey: user?.pubkey });
  const { mutate: updatePlaylist, isPending } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const trackRef = `${MUSIC_TRACK_KIND}:${track.pubkey}:${track.id}`;

  const handleAddToPlaylist = async (playlistId: string) => {
    const playlist = playlists?.find((p) => p.id === playlistId);
    if (!playlist) return;

    // Set this playlist as currently updating
    setUpdatingPlaylistId(playlistId);

    // Check if track already exists
    if (playlist.trackRefs.includes(trackRef)) {
      toast({
        title: 'Already in playlist',
        description: 'This track is already in the playlist',
      });
      return;
    }

    // Create updated playlist with new track
    const tags: string[][] = [
      ['d', playlist.id],
      ['title', playlist.title],
      ['t', 'playlist'],
      ['alt', `Playlist: ${playlist.title}`],
    ];

    if (playlist.description) {
      tags.push(['description', playlist.description]);
    }

    if (playlist.imageUrl) {
      tags.push(['image', playlist.imageUrl]);
    }



    // Add existing tracks
    playlist.trackRefs.forEach((ref) => {
      tags.push(['a', ref]);
    });

    // Add new track
    tags.push(['a', trackRef]);

    // Add category tags
    playlist.tags.forEach((tag) => {
      tags.push(['t', tag]);
    });

    if (playlist.isPublic) {
      tags.push(['public', 'true']);
    }

    if (playlist.isCollaborative) {
      tags.push(['collaborative', 'true']);
    }

    updatePlaylist(
      {
        kind: PLAYLIST_KIND,
        content: playlist.description || '',
        tags,
      },
      {
        onSuccess: async () => {
          // Invalidate and refetch playlists to ensure cache is fresh
          await queryClient.invalidateQueries({
            queryKey: ['playlists', user?.pubkey],
          });

          // Also invalidate playlist-tracks queries that might be affected
          await queryClient.invalidateQueries({
            queryKey: ['playlist-tracks', playlistId],
          });

          // Wait for the refetch to complete
          await refetch();

          toast({
            title: 'Success',
            description: `Added to "${playlist.title}"`,
          });

          // Clear updating state
          setUpdatingPlaylistId(null);

          // Don't close dialog immediately to allow rapid additions
          // setOpen(false);
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to add track to playlist',
            variant: 'destructive',
          });

          // Clear updating state on error
          setUpdatingPlaylistId(null);
        },
      }
    );
  };

  if (!user) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <ListPlus className="mr-2 h-4 w-4" />
            Add to Playlist
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add to Playlist</DialogTitle>
          <DialogDescription>
            Choose a playlist to add "{track.title}"
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-4">
            <CreatePlaylistDialog onSuccess={() => refetch()} />
          </div>

          <ScrollArea className="h-[300px] pr-4">
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="backdrop-blur-md border-white/[0.11]">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2 flex-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-48" />
                        </div>
                        <Skeleton className="h-9 w-16" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : playlists && playlists.length > 0 ? (
              <div className="space-y-2">
                {playlists.map((playlist) => {
                  const hasTrack = playlist.trackRefs.includes(trackRef);
                  const isUpdating = updatingPlaylistId === playlist.id;
                  const isAnyUpdating = updatingPlaylistId !== null;

                  return (
                    <Card key={playlist.id} className="backdrop-blur-md border-white/[0.11]">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 mr-4">
                            <h4 className="font-medium truncate">{playlist.title}</h4>
                            <p className="text-sm text-muted-foreground truncate">
                              {playlist.trackRefs.length} track{playlist.trackRefs.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleAddToPlaylist(playlist.id)}
                            disabled={isAnyUpdating || hasTrack}
                          >
                            {isUpdating ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : hasTrack ? (
                              'Added'
                            ) : (
                              <>
                                <Plus className="mr-1 h-4 w-4" />
                                Add
                              </>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <p className="text-muted-foreground mb-4">You don't have any playlists yet</p>
                <p className="text-sm text-muted-foreground">Create your first playlist to get started</p>
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
